import { useState } from 'react';
import { parseMoneyInput } from '@/lib/subscriptionPlans';
import type { Besoin } from '@/lib/besoins';

interface Props {
  besoin: Besoin;
  onClose: () => void;
  onAcceptBudget: () => Promise<void>;
  onPropose: (prix: number, message: string) => Promise<void>;
}

/** Professionnel : accepter le prix du demandeur, ou proposer le sien. */
export default function ProposerModal({ besoin, onClose, onAcceptBudget, onPropose }: Props) {
  const budget = Number(besoin.budget) || 0;
  const devise = besoin.monaie || 'MAD';
  const [mode, setMode] = useState<'choix' | 'offre'>(budget > 0 ? 'choix' : 'offre');
  const [prixText, setPrixText] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      await onAcceptBudget();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action impossible pour le moment.');
      setBusy(false);
    }
  };

  const sendOffer = async () => {
    const prix = parseMoneyInput(prixText);
    if (!prix) {
      setError('Indiquez votre prix.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onPropose(prix, message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action impossible pour le moment.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70">
          <div className="min-w-0">
            <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{besoin.titre}</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {besoin.categorie || 'Besoin'} {besoin.ville ? `· ${besoin.ville}` : ''} · par {besoin.auteur_nom || 'un membre Zifek'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {besoin.description && (
            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{besoin.description}</p>
          )}

          {budget > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-accent-50 border border-accent-200 rounded-lg">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-accent-900 uppercase tracking-wide">Prix proposé par le demandeur</p>
                <p className="text-lg font-bold text-foreground-950">
                  {budget.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {devise}
                </p>
              </div>
              <i className="ri-hand-coin-line text-2xl text-accent-600"></i>
            </div>
          )}

          {mode === 'choix' ? (
            <div className="space-y-3">
              <button
                onClick={accept}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-double-line"></i>}
                J&apos;accepte ce prix — je prends la mission
              </button>
              <button
                onClick={() => setMode('offre')}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
              >
                <i className="ri-price-tag-3-line"></i>
                Proposer mon propre prix
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre prix</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={prixText}
                    onChange={(e) => setPrixText(e.target.value)}
                    placeholder="ex. 2500"
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Devise</label>
                  <div className="w-full px-3 py-2.5 bg-background-200/50 border border-background-200/70 rounded-lg text-sm text-foreground-700">
                    {devise}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Message (facultatif)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 300))}
                  rows={3}
                  placeholder="Délai, conditions, disponibilité..."
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>
              <div className="flex gap-3">
                {budget > 0 && (
                  <button
                    onClick={() => setMode('choix')}
                    disabled={busy}
                    className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
                  >
                    Retour
                  </button>
                )}
                <button
                  onClick={sendOffer}
                  disabled={busy}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                >
                  {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                  Envoyer ma proposition
                </button>
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}